import type { ApiRequestBody, ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetIncidentListQuery = ApiSearchQuery<'/v2/icd-force/incidents', 'get'>;
export type GetIncidentListResponse = ApiResponseBody<'/v2/icd-force/incidents', 'get'>;
export type IncidentListItem = GetIncidentListResponse['items'][number];
export type IncidentTriggerProductionLabel = NonNullable<IncidentListItem['triggerProduction']>;
export type IncidentOrderBy = NonNullable<GetIncidentListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetIncidentCountQuery = ApiSearchQuery<'/v2/icd-force/incidents/count', 'get'>;
export type GetIncidentCountResponse = ApiResponseBody<'/v2/icd-force/incidents/count', 'get'>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

/** インシデント詳細本体（targetNodeIds / usedProducts / triggerProduction 含む）。 */
export type GetIncidentDetailResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}', 'get'>;

/** 影響リリース一覧（productions）。 */
export type GetIncidentProductionsResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/productions', 'get'>;
export type IncidentProductionItem = GetIncidentProductionsResponse['items'][number];

/** 関連不具合一覧（issues）。 */
export type GetIncidentIssuesResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/issues', 'get'>;
export type IncidentIssueItem = GetIncidentIssuesResponse['items'][number];

/** 影響顧客一覧（customers）。 */
export type GetIncidentCustomersResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/customers', 'get'>;
export type IncidentCustomerItem = GetIncidentCustomersResponse['items'][number];

/**
 * インシデント本体（overview スカラー）の部分更新リクエスト。
 * 影響範囲の串刺し（影響機能 / 影響リリース / 関連不具合 / 影響顧客）は
 * 専用の POST / DELETE で扱うため、本 body には含まれない。
 */
export type PatchIncidentRequest = ApiRequestBody<'/v2/icd-force/incidents/{id}', 'patch'>;
/** PATCH レスポンスは詳細 GET と同 shape の Incident オブジェクト。 */
export type PatchIncidentResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}', 'patch'>;

// ---------------------------------------------------------------------------
// Link / Unlink API Types
// (影響機能 / 影響リリース / 関連不具合 / 影響顧客の紐付け追加・解除)
// ---------------------------------------------------------------------------

export type AddIncidentTargetNodesRequest = ApiRequestBody<'/v2/icd-force/incidents/{id}/target-nodes', 'post'>;
export type AddIncidentTargetNodesResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/target-nodes', 'post'>;
export type RemoveIncidentTargetNodeResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/target-nodes/{targetNodeId}', 'delete'>;
export type AddIncidentProductionsRequest = ApiRequestBody<'/v2/icd-force/incidents/{id}/productions', 'post'>;
export type AddIncidentProductionsResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/productions', 'post'>;
export type RemoveIncidentProductionResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/productions/{productionId}', 'delete'>;
export type AddIncidentIssuesRequest = ApiRequestBody<'/v2/icd-force/incidents/{id}/issues', 'post'>;
export type AddIncidentIssuesResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/issues', 'post'>;
export type RemoveIncidentIssueResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/issues/{issueId}', 'delete'>;
export type AddIncidentCustomersRequest = ApiRequestBody<'/v2/icd-force/incidents/{id}/customers', 'post'>;
export type AddIncidentCustomersResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/customers', 'post'>;
export type RemoveIncidentCustomerResponse = ApiResponseBody<'/v2/icd-force/incidents/{id}/customers/{customerId}', 'delete'>;

// ---------------------------------------------------------------------------
// Incident Enums (BE 由来)
// ---------------------------------------------------------------------------

/** 重大度 SEV1（重大）〜 SEV4（低）。 */
export const IncidentSeverity = {
  SEV1: 'SEV1',
  SEV2: 'SEV2',
  SEV3: 'SEV3',
  SEV4: 'SEV4'
} as const;
export type IncidentSeverity = (typeof IncidentSeverity)[keyof typeof IncidentSeverity];
const INCIDENT_SEVERITY_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentSeverity));
export function isIncidentSeverity(value: string | null | undefined): value is IncidentSeverity {
  return value != null && INCIDENT_SEVERITY_VALUES.has(value);
}

/** 社内対応ステータス（検知 → クローズ / 取消の 8 値）。 */
export const IncidentInternalStatus = {
  DETECTED: 'DETECTED',
  TRIAGED: 'TRIAGED',
  INVESTIGATING: 'INVESTIGATING',
  IDENTIFIED: 'IDENTIFIED',
  MITIGATED: 'MITIGATED',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
  CANCELED: 'CANCELED'
} as const;
export type IncidentInternalStatus = (typeof IncidentInternalStatus)[keyof typeof IncidentInternalStatus];
const INCIDENT_INTERNAL_STATUS_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentInternalStatus));
export function isIncidentInternalStatus(value: string | null | undefined): value is IncidentInternalStatus {
  return value != null && INCIDENT_INTERNAL_STATUS_VALUES.has(value);
}

/** 分類（障害 / 劣化 / セキュリティ / データ / 外部要因）。 */
export const IncidentCategory = {
  FAILURE: 'FAILURE',
  DEGRADATION: 'DEGRADATION',
  SECURITY: 'SECURITY',
  DATA: 'DATA',
  EXTERNAL: 'EXTERNAL'
} as const;
export type IncidentCategory = (typeof IncidentCategory)[keyof typeof IncidentCategory];
const INCIDENT_CATEGORY_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentCategory));
export function isIncidentCategory(value: string | null | undefined): value is IncidentCategory {
  return value != null && INCIDENT_CATEGORY_VALUES.has(value);
}

/** 優先度（P1 緊急 〜 P5 計画）。 */
export const IncidentPriority = {
  P1: 'P1',
  P2: 'P2',
  P3: 'P3',
  P4: 'P4',
  P5: 'P5'
} as const;
export type IncidentPriority = (typeof IncidentPriority)[keyof typeof IncidentPriority];
const INCIDENT_PRIORITY_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentPriority));
export function isIncidentPriority(value: string | null | undefined): value is IncidentPriority {
  return value != null && INCIDENT_PRIORITY_VALUES.has(value);
}

/** 影響度（高 / 中 / 低）。`null` 解除可。 */
export const IncidentImpact = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const;
export type IncidentImpact = (typeof IncidentImpact)[keyof typeof IncidentImpact];
const INCIDENT_IMPACT_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentImpact));
export function isIncidentImpact(value: string | null | undefined): value is IncidentImpact {
  return value != null && INCIDENT_IMPACT_VALUES.has(value);
}

/** 緊急度（高 / 中 / 低）。`null` 解除可。 */
export const IncidentUrgency = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const;
export type IncidentUrgency = (typeof IncidentUrgency)[keyof typeof IncidentUrgency];
const INCIDENT_URGENCY_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentUrgency));
export function isIncidentUrgency(value: string | null | undefined): value is IncidentUrgency {
  return value != null && INCIDENT_URGENCY_VALUES.has(value);
}

/** 起票元システム（監視 / オンコール / 手動 / 顧客報告）。指定時のみ更新（`null` 不可）。 */
export const IncidentSourceSystem = {
  MONITORING: 'MONITORING',
  ON_CALL: 'ON_CALL',
  MANUAL: 'MANUAL',
  CUSTOMER_REPORT: 'CUSTOMER_REPORT'
} as const;
export type IncidentSourceSystem = (typeof IncidentSourceSystem)[keyof typeof IncidentSourceSystem];
const INCIDENT_SOURCE_SYSTEM_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentSourceSystem));
export function isIncidentSourceSystem(value: string | null | undefined): value is IncidentSourceSystem {
  return value != null && INCIDENT_SOURCE_SYSTEM_VALUES.has(value);
}

/** 検知手段（自動監視 / 顧客報告 / 内部発見）。`null` 解除可。 */
export const IncidentDetectionMethod = {
  AUTO_MONITORING: 'AUTO_MONITORING',
  CUSTOMER_REPORT: 'CUSTOMER_REPORT',
  INTERNAL_DISCOVERY: 'INTERNAL_DISCOVERY'
} as const;
export type IncidentDetectionMethod = (typeof IncidentDetectionMethod)[keyof typeof IncidentDetectionMethod];
const INCIDENT_DETECTION_METHOD_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentDetectionMethod));
export function isIncidentDetectionMethod(value: string | null | undefined): value is IncidentDetectionMethod {
  return value != null && INCIDENT_DETECTION_METHOD_VALUES.has(value);
}

/** 原因カテゴリ（コード / 設定 / インフラ / 外部依存 / 人為 / データ）。`null` 解除可。 */
export const IncidentRootCauseCategory = {
  CODE: 'CODE',
  CONFIG: 'CONFIG',
  INFRASTRUCTURE: 'INFRASTRUCTURE',
  EXTERNAL_DEPENDENCY: 'EXTERNAL_DEPENDENCY',
  HUMAN: 'HUMAN',
  DATA: 'DATA'
} as const;
export type IncidentRootCauseCategory = (typeof IncidentRootCauseCategory)[keyof typeof IncidentRootCauseCategory];
const INCIDENT_ROOT_CAUSE_CATEGORY_VALUES: ReadonlySet<string> = new Set(Object.values(IncidentRootCauseCategory));
export function isIncidentRootCauseCategory(value: string | null | undefined): value is IncidentRootCauseCategory {
  return value != null && INCIDENT_ROOT_CAUSE_CATEGORY_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Status Tab Buckets
//
// 一覧のステータスタブは internalStatus 8 値を 5 バケットに集約したもの。
// BE の一覧 / カウント API の statusTab（all/open/resolved/closed/canceled）と
// 1:1 対応する。バケットの中身（特に open = 5 値の集合）の解決は BE 側が担い、
// FE はタブ値をそのまま statusTab として渡す。
//   - all      : フィルタ無し（全件）
//   - open     : DETECTED / TRIAGED / INVESTIGATING / IDENTIFIED / MITIGATED
//   - resolved : RESOLVED
//   - closed   : CLOSED
//   - canceled : CANCELED
// ---------------------------------------------------------------------------

export const IncidentStatusTab = {
  ALL: 'all',
  OPEN: 'open',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
  CANCELED: 'canceled'
} as const;
export type IncidentStatusTab = (typeof IncidentStatusTab)[keyof typeof IncidentStatusTab];